import React from 'react';

import Controller, { OnAfterFetch, OnClick, OnDynamicContentRender, OnMount, OnSelectItem, OnSubmit, OnUnselectItem } from '@philips-internal/oswald-ui-core/lib/client';
import { ActionAPI, AutocompleteAPI, DeckAPI, FetchAPI, FilterAPI, PanelAPI } from '@philips-internal/oswald-ui-components';
import { FetchEvent, RenderEvent, SelectItemEvent, SubmitEvent, UnselectItemEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { BrokerService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { isMobile, isMobileResolution } from '@philips-internal/oswald-ui-utils';
import { Value } from '@philips-internal/oswald-ui-core';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import { Inject } from '@odinjs/odin';

import moment from 'moment';
import BaseController from '../BaseController';
import { SUBSCRIBE_TOPIC } from '../../constants/subscribe-topic';
import TasksContext from '../../utils/tasks-context';
import { FragmentNames, TasksSideBarAliases } from '../../codegen';
import { ScheduleTasksProgress } from '../../interfaces/schedule-tasks-progress';
import { SCHEDULE_CODE_TYPE } from '../../constants/schedule-code-type';
import { BS_TASK_LIST_REQUEST_ABORTED, BS_TASK_LIST_VISIBLE_STATE } from '../../constants/application-constants';
import { DisplayRule } from '../../interfaces/scheduling-display-rules';

import './tasks-side-bar-controller.css';
import { TaskFilterType } from '../../constants/TaskFilterType';

export interface TasksSideBarCard {
  cardId: string;
  cdMedico: string;
  eventType: string;
  ieTipoAgenda: number;
  nrSeqAtendimento?: number;
  scheduleNumber: number;
}

interface FilterList {
  filterFromDate: string;
  progressFilter: FilterItem[];
  scheduleTypeFilter: FilterItem[];
}

interface FilterItem {
  label: string;
  value: string;
}

interface FilterParams {
  [key: string]: string | string[];
}

enum ContextKeys {
  LAST_SIDEBAR_ITEM_SELECTED = 'last_side_bar_item_selected',
}

@Controller({
  component: TasksSideBarAliases.TASKS_SIDE_BAR_LAYOUT,
  fragment: FragmentNames.TASKS_SIDE_BAR
})
class TasksSideBarController extends BaseController {

  tasksProgressList!: ScheduleTasksProgress[];
  progressFilterData: DisplayRule[] = [
    { label: MessageService.translate('patient.completed'), value: 'completed' },
    { label: MessageService.translate('patient.pending'), value: 'pending' },
  ]
  scheduleTypeFilterData: DisplayRule[] = [
    { label: MessageService.translate('schedule-type.exam'), value: TaskFilterType.EXAM },
    { label: MessageService.translate('schedule-type.consultation'), value: TaskFilterType.CONSULTATION },
    { label: MessageService.translate('patient.label.surgery'), value: TaskFilterType.SURGERY },
    { label: MessageService.translate('patient.label.attendance'), value: TaskFilterType.ATTENDANCE}
  ]

  @Inject(TasksSideBarAliases.TASKS_PROGRESS_DECK)
    tasksProgressDeckApi!: DeckAPI;

  @Inject(TasksSideBarAliases.VIEW_ALL_ACTION)
    viewAllActionApi!: ActionAPI;

  @Inject(TasksSideBarAliases.TASKS_PROGRESS_FETCH_PANEL)
    tasksProgressFetchPanelApi!: PanelAPI;

  @Inject(TasksSideBarAliases.FILTER)
    filterApi!: FilterAPI;

  @Inject(TasksSideBarAliases.PROGRESS_FILTER_AUTOCOMPLETE)
    progressfilterAutocompleteApi!: AutocompleteAPI;

  @Inject(TasksSideBarAliases.SCHEDULE_TYPE_AUTOCOMPLETE)
    scheduleTypeAutocompleteApi!: AutocompleteAPI;

  @Inject(TasksSideBarAliases.TASKS_PROGRESS_FETCH)
    tasksProgressFetchApi!: FetchAPI;

  @OnAfterFetch(TasksSideBarAliases.TASKS_PROGRESS_FETCH)
  onAfterFetch(event: FetchEvent): void {
    const fetchData = event.data as ScheduleTasksProgress[];
    const deckData = fetchData.map(schedule => {
      const isExamSchedule = schedule.ieTipoAgenda === SCHEDULE_CODE_TYPE.EXAM;
      const dateFilter = moment(schedule.dsDate).format('ll');

      return {
        calculatedBlockedTasks: schedule.calculatedBlockedTasks,
        cardId: `${schedule.nrSeqAgenda || schedule.nrSeqAtendimento}.${schedule.typeCadEvent}`,
        cdMedico: schedule.cdMedico,
        completedTasks: schedule.tarefasConcluidas,
        date: schedule.dsDate,
        dateFilter,
        eventType: schedule.typeCadEvent,
        ieTipoAgenda: schedule.ieTipoAgenda,
        nrSeqAtendimento: schedule.nrSeqAtendimento,
        scheduleNumber: schedule.nrSeqAgenda,
        title: isExamSchedule ? (schedule.dsTitulo ?? '-') : this.getAppointmentTitle(schedule.dsTitulo,schedule.dsSubTitulo),
        totalTasks: schedule.totalTarefas,
      }
    });

    this.tasksProgressList = fetchData;
    this.tasksProgressDeckApi.setData(deckData);
  }

  getAppointmentTitle(dsTitulo: string, dsSubTitulo: string): string {
    if (dsTitulo && dsSubTitulo) {
      return `${dsTitulo} - ${dsSubTitulo}`;
    }

    if (!dsSubTitulo && dsTitulo) {
      return `${dsTitulo} - ${MessageService.translate('schedule-type.consultation')}`;
    }

    if (dsSubTitulo && !dsTitulo) {
      return dsSubTitulo;
    }

    return MessageService.translate('schedule-type.consultation');
  }

  @OnUnselectItem(TasksSideBarAliases.TASKS_PROGRESS_DECK)
  handleUnselectSidebarItem(event: UnselectItemEvent<TasksSideBarCard>): void {
    this.tasksProgressDeckApi.selectCard(event.item.cardId);
  }

  @OnSelectItem(TasksSideBarAliases.TASKS_PROGRESS_DECK)
  onSelectSideBarCard(event: SelectItemEvent<TasksSideBarCard>): void {
    const sideBarCardData = event.item;
    if (this.context.get<string>(ContextKeys.LAST_SIDEBAR_ITEM_SELECTED) === sideBarCardData.cardId) {
      return;
    }

    this.selectSidebarCardItem(sideBarCardData);
  }

  private selectSidebarCardItem(sideBarCardData: TasksSideBarCard): void {
    const { tasksProgressDeckApi, showTaskList } = this;
    TasksContext.cdMedico = sideBarCardData.cdMedico;
    TasksContext.nrSeqAgenda = sideBarCardData.scheduleNumber;
    TasksContext.nrAtendimento = sideBarCardData.nrSeqAtendimento;
    TasksContext.eventType = sideBarCardData.eventType;
    TasksContext.ieTipoAgenda = sideBarCardData.ieTipoAgenda;
    TasksContext.selectedCardId = sideBarCardData.cardId;
    this.context.set({ [ContextKeys.LAST_SIDEBAR_ITEM_SELECTED]: sideBarCardData.cardId });

    BrokerService.publishToTopic(SUBSCRIBE_TOPIC.TASKS_SIDE_BAR, sideBarCardData);

    BrokerService.subscribeToTopic(sideBarCardData.cardId, (completedTasks: number) => {
      const updatedDeckData = tasksProgressDeckApi.getData().map(task => {
        const { scheduleNumber, nrSeqAtendimento, eventType } = task as unknown as TasksSideBarCard;
        if (((scheduleNumber && scheduleNumber === sideBarCardData.scheduleNumber)
           || (nrSeqAtendimento && nrSeqAtendimento === sideBarCardData.nrSeqAtendimento))
           && eventType === sideBarCardData.eventType) {
          return { ...task, completedTasks }
        }

        return task;
      });

      tasksProgressDeckApi.setData(updatedDeckData);
    })

    if (isMobileResolution()) {
      showTaskList(false);
    }
  }

  @OnClick(TasksSideBarAliases.VIEW_ALL_ACTION)
  onClickViewAllAction(): void {
    BrokerService.publishToTopic(BS_TASK_LIST_REQUEST_ABORTED, true);
    this.showTaskList(true);
  }

  showTaskList = (value: boolean): void => {
    if (isMobile() || isMobileResolution()) {
      TasksContext.eventType = '';
      this.viewAllActionApi.setEnabled(!value);
      this.tasksProgressFetchPanelApi.setVisible(value);
      this.notifyTaskListVisible(value);
    }
  }

  private notifyTaskListVisible(isTaskListVisible: boolean): void {
    BrokerService.publishToTopic(BS_TASK_LIST_VISIBLE_STATE, isTaskListVisible);
  }

  setParamFilter(params: FilterParams, value: string | string[], field: string): void {
    if (value != null) {
      params[field] = value;
    }
  }

  @OnMount(TasksSideBarAliases.FILTER)
  onFilterMount(): void {
    const dateToday = moment.utc(TasksContext.taskDateToFilter).format(ISO_DATE_FORMAT);

    this.filterApi.setData({
      filterFromDate: dateToday,
      progressFilter: this.progressFilterData,
      scheduleTypeFilter: this.scheduleTypeFilterData
    });
  }

  @OnMount(TasksSideBarAliases.PROGRESS_FILTER_AUTOCOMPLETE)
  onProgressFilterAutocompleteMount(): void {
    const progressFilter = this.progressFilterData as unknown as Record<string, Value>[];

    this.progressfilterAutocompleteApi.setData(progressFilter);
  }

  @OnMount(TasksSideBarAliases.SCHEDULE_TYPE_AUTOCOMPLETE)
  onScheduleTypeAutocompleteMount(): void {
    const scheduleTypeFilter = this.scheduleTypeFilterData as unknown as Record<string, Value>[];

    this.scheduleTypeAutocompleteApi.setData(scheduleTypeFilter);
  }

  @OnSubmit(TasksSideBarAliases.FILTER)
  onSubmitFilter(event: SubmitEvent): void {
    const record = event.record as FilterList;
    const params = {};

    this.setParamFilter(params, record.filterFromDate, 'filterFromDate');
    this.setParamFilter(params, record.progressFilter?.map(item => item.value), 'progressFilter');
    this.setParamFilter(params, record.scheduleTypeFilter?.map(item => item.value), 'scheduleTypeFilter');

    this.tasksProgressFetchApi.setParameters(params)
  }

  @OnDynamicContentRender(TasksSideBarAliases.TASKS_PROGRESS_DECK)
  onDeckGroupRender(event: RenderEvent): void {
    if (event?.entry != null) {
      const { render } = event;

      render(() => {
        return (
          <div className='deck-title-tasks-side-bar-controller'  >
            <span>{event.entry}</span>
          </div>
        );
      })
    }
  }
}

export default TasksSideBarController;